import { url } from "./url";
import { fetchWithAuth } from "./FetchWithAuth";

function buildFormData(payload = {}) {
  const fd = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      fd.append(key, value);
    }
  });
  return fd;
}

export async function fetchAcademics(enquiryId) {
  const response = await fetchWithAuth(`${url}/enquiry/academics/${enquiryId}`, {
    method: "GET",
    cache: "no-store",
  });
  return response.json();
}

export async function getEnquiryStatusHistory(enquiryId) {
  const response = await fetchWithAuth(`${url}/enquiry/status-history/${enquiryId}`, {
    method: "GET",
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "Failed to load status history");
  return data.status_history || [];
}

/** Application and visa counsellor names for an enquiry (loaded from DB by enquiry id). */
export async function getEnquiryAssignment(enquiryId) {
  const response = await fetchWithAuth(`${url}/enquiry/${enquiryId}/assignment`, {
    method: "GET",
    credentials: "include",
    cache: "no-store",
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "Failed to load assignment");
  return data;
}

/** Full chronological activity trail (comments, payments, sessions, status changes, etc.). */
export async function getEnquiryActivityTrail(enquiryId) {
  const response = await fetchWithAuth(`${url}/enquiry/activity-trail/${enquiryId}`, {
    method: "GET",
    cache: "no-store",
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "Failed to load activity trail");
  return data.activities || [];
}

export async function fetchApplicationDocuments(enquiryId) {
  try {
    const response = await fetchWithAuth(`${url}/application/get-documents/${enquiryId}`, {
      method: "GET",
    });
    return response.json();
  } catch (error) {
    if (error?.status === 404) {
      return { documents: [] };
    }
    throw error;
  }
}

export async function uploadApplicationDocument({ enquiryId, name, type, file }) {
  const response = await fetch(`${url}/application/upload-file`, {
    method: "POST",
    body: buildFormData({
      name,
      type,
      applicationID: enquiryId,
      file,
    }),
    credentials: "include",
  });
  if (response.status === 401) {
    window.location.href = "/";
    return null;
  }
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || "Upload failed");
  }
  return response.json();
}

export async function reuploadApplicationDocument({ enquiryId, fileId, filename, file }) {
  const response = await fetch(`${url}/application/reupload-document`, {
    method: "POST",
    body: buildFormData({
      file,
      filename,
      file_id: fileId,
      enquiry_id: enquiryId,
    }),
    credentials: "include",
  });
  if (response.status === 401) {
    window.location.href = "/";
    return null;
  }
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || "Reupload failed");
  }
  return response.json();
}

export async function deleteApplicationDocument(fileId) {
  const response = await fetchWithAuth(`${url}/application/delete-document/${fileId}`, {
    method: "DELETE",
  });
  return response.json();
}

/** Assign a course to an enquiry (creates a student_program). Payload: { course_id, enquiry_id, intake?, status? } */
export async function addCoursePreference(payload) {
  const response = await fetchWithAuth(`${url}/enquiry/academics/course_preference`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return response.json();
}

export async function deleteAcademicItem(id, type) {
  const response = await fetchWithAuth(`${url}/enquiry/academics/delete`, {
    method: "POST",
    body: JSON.stringify({ id, type }),
  });
  return response.json();
}

/** Mark a course preference (application) as submitted for visa. */
export async function setApplicationSubmittedForVisa(coursePreferenceId) {
  const response = await fetchWithAuth(
    `${url}/application/${coursePreferenceId}/submitted-for-visa`,
    { method: "PATCH" }
  );
  return response.json();
}

/** Update status of a student program (application) by its id. */
export async function updateApplicationStatus(applicationId, status) {
  const response = await fetchWithAuth(`${url}/enquiry/update_application_status`, {
    method: "POST",
    body: JSON.stringify({ id: applicationId, status }),
  });
  return response.json();
}

export async function getUniversities(params = {}) {
  const q = new URLSearchParams();
  if (params.search) q.set("search", params.search);
  if (params.status && params.status !== "all") q.set("status", params.status);
  if (params.includeUsage) q.set("include_usage", "true");
  if (params.date_from) q.set("date_from", params.date_from);
  if (params.date_to) q.set("date_to", params.date_to);
  const response = await fetchWithAuth(`${url}/universities?${q.toString()}`, { method: "GET" });
  return response.json();
}

/** Create a program (or university). Payload: { name, description?, logo_url?, website? } */
export async function addUniversity(name) {
  const payload = typeof name === "string" ? { name } : name;
  const response = await fetchWithAuth(`${url}/universities`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return response.json();
}

/** Programs — Flask returns { programs: [...] }; normalize to an array. */
export async function getPrograms(params = {}) {
  const q = new URLSearchParams();
  if (params.search) q.set("search", params.search);
  if (params.includeUsage) q.set("include_usage", "true");
  if (params.date_from) q.set("date_from", params.date_from);
  if (params.date_to) q.set("date_to", params.date_to);
  const response = await fetchWithAuth(`${url}/programs?${q.toString()}`, { method: "GET" });
  const data = await response.json();
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.programs)) return data.programs;
  if (Array.isArray(data?.universities)) return data.universities;
  return [];
}

export async function addProgram(payload) {
  const response = await fetchWithAuth(`${url}/programs`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return response.json();
}

/** Programs with nested courses; optional search + pagination (server-side). */
export async function getProgramsCourseOverview(params = {}) {
  const searchParams = new URLSearchParams();
  if (params.search != null && String(params.search).trim() !== "") {
    searchParams.set("search", String(params.search).trim());
  }
  if (params.page != null) searchParams.set("page", String(params.page));
  if (params.page_size != null) searchParams.set("page_size", String(params.page_size));
  const qs = searchParams.toString();
  const path = qs ? `${url}/programs/course-overview?${qs}` : `${url}/programs/course-overview`;
  const response = await fetchWithAuth(path, { method: "GET" });
  return response.json();
}

/** Courses under a program — Flask returns { courses: [...] }; normalize to an array. */
export async function getProgramCourses(programId) {
  const response = await fetchWithAuth(`${url}/programs/${programId}/courses`, { method: "GET" });
  const data = await response.json();
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.courses)) return data.courses;
  return [];
}

/** Merged built-in + DB unit session type labels for program unit rows. */
export async function getUnitSessionTypes() {
  const response = await fetchWithAuth(`${url}/unit-session-types`, { method: "GET" });
  const data = await response.json();
  if (Array.isArray(data?.types)) return data.types;
  return [];
}

export async function createUnitSessionType(name) {
  const response = await fetchWithAuth(`${url}/unit-session-types`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  return response.json();
}

export async function addCourseToProgram(programId, payload) {
  const response = await fetchWithAuth(`${url}/programs/${programId}/courses`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return response.json();
}

export async function updateCourse(courseId, payload) {
  const response = await fetchWithAuth(`${url}/courses/${courseId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return response.json();
}

export async function deleteCourse(courseId) {
  const response = await fetchWithAuth(`${url}/courses/${courseId}`, { method: "DELETE" });
  return response.json();
}

export async function updateUniversity(id, payload) {
  const response = await fetchWithAuth(`${url}/universities/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return response.json();
}

export async function deleteUniversity(id) {
  const response = await fetchWithAuth(`${url}/universities/${id}`, {
    method: "DELETE",
  });
  return response.json();
}

export async function bulkApproveUniversities(ids = []) {
  const response = await fetchWithAuth(`${url}/universities/bulk-approve`, {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
  return response.json();
}

export async function getUniversityStats() {
  const response = await fetchWithAuth(`${url}/universities/stats`, {
    method: "GET",
  });
  return response.json();
}

export async function getProgramsAnalytics(params = {}) {
  const q = new URLSearchParams();
  if (params.start_date) q.set("start_date", params.start_date);
  if (params.end_date) q.set("end_date", params.end_date);
  if (params.interval) q.set("interval", params.interval);
  if (params.compare != null) q.set("compare", params.compare ? "true" : "false");
  if (params.top_n != null) q.set("top_n", String(params.top_n));
  (params.program_ids || []).forEach((id) => q.append("program_ids[]", String(id)));
  (params.course_ids || []).forEach((id) => q.append("course_ids[]", String(id)));
  const response = await fetchWithAuth(`${url}/programs/analytics?${q.toString()}`, {
    method: "GET",
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "Failed to load programs analytics");
  return data;
}

/**
 * Download universities Excel report (Overview with AI summary, Totals, one sheet per university).
 * @param {Object} params - { date_from?: 'YYYY-MM-DD', date_to?: 'YYYY-MM-DD' }
 * @returns {Promise<void>}
 */
export async function downloadUniversitiesExcelReport(params = {}) {
  const q = new URLSearchParams();
  if (params.date_from) q.set("date_from", params.date_from);
  if (params.date_to) q.set("date_to", params.date_to);
  const response = await fetchWithAuth(`${url}/universities/excel-report?${q.toString()}`, {
    method: "GET",
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || "Failed to download report");
  }
  const blob = await response.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "universities_report.xlsx";
  a.click();
  URL.revokeObjectURL(a.href);
}

export async function searchInstitutionLogos(query) {
  const normalizedQuery = (query || "").trim();
  if (!normalizedQuery) return [];

  const logoDevApiKey = process.env.REACT_APP_LOGODEV_API_KEY || "sk_ShToH1grTkyiaICpVLPuZg";
  const headers = logoDevApiKey ? { Authorization: logoDevApiKey } : {};
  const response = await fetch(`https://api.logo.dev/search?q=${encodeURIComponent(normalizedQuery)}`, {
    headers,
  });
  if (!response.ok) {
    throw new Error("Failed to fetch logo suggestions.");
  }
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}
